import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft, EyeOff, Trash2, Check, HeartHandshake, MessageCircle, Lock } from "lucide-react";
import { yanaAdminList, yanaSetVisible, yanaDelete, yanaMessages, yanaMessageHandled, yanaMessageDelete, getMe, mediaUrl } from "../lib/api";
import { toast } from "sonner";

export default function YanaAdmin() {
  const [friends, setFriends] = useState(null);
  const [messages, setMessages] = useState([]);
  const [authed, setAuthed] = useState(null);

  const load = () => {
    yanaAdminList().then((d) => setFriends(d.friends || [])).catch(() => setFriends([]));
    yanaMessages().then((d) => setMessages(d.messages || [])).catch(() => setMessages([]));
  };

  useEffect(() => {
    getMe().then(() => { setAuthed(true); load(); }).catch(() => setAuthed(false));
  }, []);

  const setVisible = async (f, visible) => {
    await yanaSetVisible(f.id, visible);
    setFriends((prev) => prev.map((x) => (x.id === f.id ? { ...x, visible } : x)));
    toast.success(visible ? "Approved — now public" : "Hidden from the public page");
  };
  const remove = async (f) => {
    if (!window.confirm(`Remove ${f.name}? This can't be undone.`)) return;
    await yanaDelete(f.id);
    setFriends((prev) => prev.filter((x) => x.id !== f.id));
    toast.success("Removed");
  };
  const markHandled = async (m) => {
    await yanaMessageHandled(m.id, !m.handled);
    setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, handled: !m.handled } : x)));
  };
  const delMessage = async (m) => {
    await yanaMessageDelete(m.id);
    setMessages((prev) => prev.filter((x) => x.id !== m.id));
    toast.success("Removed");
  };

  if (authed === false)
    return (
      <div className="grid h-screen place-items-center bg-background text-center text-white">
        <div><p className="font-display text-2xl">Please sign in</p>
          <a href="/" className="mt-6 inline-block bg-accent px-6 py-3 font-body text-sm font-semibold text-black">Go to login</a></div>
      </div>
    );
  if (friends === null)
    return <div className="grid h-screen place-items-center bg-background text-accent"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const pending = friends.filter((f) => !f.visible);
  const live = friends.filter((f) => f.visible);

  return (
    <div className="min-h-screen bg-background text-white" data-testid="yana-admin-page">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <span className="flex items-center gap-2 font-display text-xl tracking-wide"><HeartHandshake className="h-5 w-5 text-primary" /> YANA Friends</span>
          <Link to="/public-settings" className="flex items-center gap-1 font-mono text-xs text-accent hover:underline"><ArrowLeft className="h-3.5 w-3.5" /> Settings</Link>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-8">
        <p className="font-body text-sm text-muted-foreground">Approve people before they appear publicly. You can hide or remove anyone at any time.</p>

        {/* Private message inbox */}
        <div className="mt-8" data-testid="yana-inbox">
          <h2 className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground">
            <MessageCircle className="h-4 w-4 text-primary" /> Private reach-outs ({messages.filter((m) => !m.handled).length} new)
          </h2>
          <p className="mt-1 font-body text-xs text-muted-foreground">People messaged someone who signed up privately. Connect them: reply to the person, then share the friend's contact when appropriate.</p>
          {messages.length === 0 ? (
            <p className="mt-2 font-body text-sm text-muted-foreground">No private messages yet.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {messages.map((m) => (
                <div key={m.id} data-testid={`inbox-msg-${m.id}`} className={`border p-4 ${m.handled ? "border-border opacity-60" : "border-primary/40 bg-primary/[0.05]"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-body text-sm"><span className="font-semibold">{m.from_name || "Someone"}</span> reached out to <span className="font-semibold text-primary">{m.friend_name}</span>
                        {m.topics?.length > 0 && <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground"> · {m.topics.join(" · ")}</span>}
                      </p>
                      <p className="mt-2 whitespace-pre-wrap border-l-2 border-primary/40 pl-3 font-body text-sm text-muted-foreground">{m.message}</p>
                      <p className="mt-2 font-mono text-[11px] text-accent">Reach them back: {m.reply_to}</p>
                      {Object.keys(m.friend_contacts || {}).length > 0 && (
                        <p className="mt-1 flex items-center gap-1 font-mono text-[11px] text-muted-foreground"><Lock className="h-3 w-3" /> {m.friend_name}'s private contact: {Object.entries(m.friend_contacts).map(([k, v]) => `${k}: ${v}`).join("  ·  ")}</p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <button data-testid={`inbox-handled-${m.id}`} onClick={() => markHandled(m)} title={m.handled ? "Mark as new" : "Mark connected"}
                              className={`inline-flex items-center gap-1 border px-2.5 py-1.5 font-mono text-[11px] ${m.handled ? "border-border text-muted-foreground" : "border-primary text-primary hover:bg-primary hover:text-white"}`}><Check className="h-3.5 w-3.5" /> {m.handled ? "Done" : "Connected"}</button>
                      <button data-testid={`inbox-del-${m.id}`} onClick={() => delMessage(m)} className="text-muted-foreground hover:text-primary"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Section title={`Pending approval (${pending.length})`} rows={pending} empty="No new signups waiting.">
          {(f) => (
            <>
              <button data-testid={`approve-${f.id}`} onClick={() => setVisible(f, true)}
                      className="inline-flex items-center gap-1 bg-primary px-3 py-1.5 font-mono text-[11px] font-semibold text-white"><Check className="h-3.5 w-3.5" /> Approve</button>
              <button data-testid={`remove-${f.id}`} onClick={() => remove(f)} className="text-muted-foreground hover:text-primary"><Trash2 className="h-4 w-4" /></button>
            </>
          )}
        </Section>

        <Section title={`Live on your page (${live.length})`} rows={live} empty="Nothing public yet.">
          {(f) => (
            <>
              <button data-testid={`hide-${f.id}`} onClick={() => setVisible(f, false)}
                      className="inline-flex items-center gap-1 border border-border px-3 py-1.5 font-mono text-[11px] hover:border-accent"><EyeOff className="h-3.5 w-3.5" /> Hide</button>
              <button data-testid={`remove-${f.id}`} onClick={() => remove(f)} className="text-muted-foreground hover:text-primary"><Trash2 className="h-4 w-4" /></button>
            </>
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, rows, empty, children }) {
  return (
    <div className="mt-8">
      <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-2 font-body text-sm text-muted-foreground">{empty}</p>
      ) : (
        <div className="mt-3 space-y-2">
          {rows.map((f) => (
            <div key={f.id} data-testid={`admin-friend-${f.id}`} className="flex items-start gap-3 border border-border p-4">
              {f.picture ? <img src={mediaUrl(f.picture)} alt={f.name} className="h-12 w-12 rounded-full object-cover" />
                : <div className="grid h-12 w-12 place-items-center rounded-full border border-primary/40 bg-primary/10 font-display text-lg text-primary">{(f.name || "?").slice(0, 1).toUpperCase()}</div>}
              <div className="min-w-0 flex-1">
                <p className="font-body text-sm font-semibold">{f.name}</p>
                <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-primary">{(f.topics || []).join(" · ")}</p>
                <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">{f.anonymous ? "anonymous" : "shows name"} · {f.contact_mode === "private" ? "private messages" : "public contacts"}</p>
                {f.bio && <p className="mt-1 font-body text-xs text-muted-foreground">{f.bio}</p>}
                <p className="mt-1 font-mono text-[10px] text-muted-foreground">{Object.entries(f.contacts || {}).map(([k, v]) => `${k}: ${v}`).join("  ·  ")}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">{children(f)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
